import { Controller } from "@hotwired/stimulus"
import { apiGet, t } from "../api_helper"
import { icon } from "../lib/icons"
import { isOfflineError, showOfflinePlaceholder } from "../lib/offline_ui"

export default class extends Controller {
  static targets = ["content"]

  connect() {
    this.load()
  }

  async load() {
    try {
      const data = await apiGet("/api/achievements")
      this.render(data)
    } catch (e) {
      if (isOfflineError(e)) { showOfflinePlaceholder(this.contentTarget, () => this.load()); return }
      console.error("Achievements load error:", e)
      this.contentTarget.innerHTML = `<div class="text-center py-8" style="color: rgb(148,163,184);">${t("achievements.error") || "Failed to load achievements"}</div>`
    }
  }

  render(data) {
    const achievements = data.achievements || []
    const earnedCount = data.earned ?? achievements.filter(a => a.earned).length
    const totalCount = data.total ?? achievements.length

    const categories = {}
    achievements.forEach(a => {
      const cat = a.category || "other"
      if (!categories[cat]) categories[cat] = []
      categories[cat].push(a)
    })

    const categoryNames = {
      transactions: t("achievements.cat_transactions") || "Transactions",
      streaks: t("achievements.cat_streaks") || "Streaks",
      budgets: t("achievements.cat_budgets") || "Budgets",
      goals: t("achievements.cat_goals") || "Goals",
      social: t("achievements.cat_social") || "Social"
    }

    let html = `
      <div class="text-center mb-5 p-4 rounded-xl" style="background-color: rgb(248,250,252);">
        <p class="text-3xl font-bold">${earnedCount} <span class="text-base font-normal" style="color: rgb(148,163,184);">/ ${totalCount}</span></p>
        <p class="text-sm mt-1" style="color: rgb(148,163,184);">${t("achievements.earned") || "Achievements earned"}</p>
      </div>
    `

    for (const [category, items] of Object.entries(categories)) {
      const catName = categoryNames[category] || category
      html += `
        <div class="mb-4">
          <p class="text-xs font-semibold uppercase tracking-wider mb-2 px-1" style="color: rgb(148,163,184);">${catName}</p>
          <div class="rounded-xl overflow-hidden" style="background-color: rgb(248,250,252);">
      `

      items.forEach((a, i) => {
        const earned = a.earned
        const opacity = earned ? "1" : "0.4"
        const border = i < items.length - 1
          ? "border-bottom: 1px solid rgb(241,245,249);"
          : ""

        const name = t(`achievements.${a.key}`) || a.name || a.key
        const desc = t(`achievements.${a.key}_desc`) || a.description || ""

        const earnedDate = earned && a.earned_at
          ? new Date(a.earned_at).toLocaleDateString(
              window.APP_LOCALE === "ru" ? "ru-RU" : "en-US",
              { day: "numeric", month: "short" }
            )
          : ""

        const iconBg = earned
          ? `background-color: ${a.color || "rgb(99,102,241)"}22;`
          : "background-color: rgba(148,163,184,0.08);"

        html += `
          <div class="flex items-center gap-3 px-4" style="padding-top: 12px; padding-bottom: 12px; ${border} opacity: ${opacity};">
            <div class="flex items-center justify-center flex-shrink-0"
                 style="width: 44px; height: 44px; border-radius: 50%; font-size: 22px; ${iconBg}">
              ${a.icon || icon("trophy", "w-6 h-6")}
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-semibold">${name}</div>
              <div class="text-xs" style="color: rgb(148,163,184);">${desc}</div>
            </div>
            ${earned
              ? `<div class="flex flex-col items-end flex-shrink-0">
                   <span style="color: rgb(16,185,129);">${icon("check", "w-4 h-4 inline")}</span>
                   ${earnedDate ? `<span class="text-[10px]" style="color: rgb(148,163,184);">${earnedDate}</span>` : ""}
                 </div>`
              : ""}
          </div>
        `
      })

      html += `</div></div>`
    }

    this.contentTarget.innerHTML = html
  }
}
